'use client';

import * as React from 'react';
import { Field } from '@base-ui/react/field';
import { Input as BaseInput } from '@base-ui/react/input';
import { clsx } from 'clsx';
import { CircleAlert } from 'lucide-react';
import styles from './Input.module.css';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label text
   */
  label?: string;

  /**
   * Helper/description text
   */
  helperText?: string;

  /**
   * Shows “(Optional)” beside the label.
   */
  optional?: boolean;

  /**
   * Error message (also sets invalid state)
   */
  error?: string;

  /**
   * Size variant
   * - md: Default 44px height
   * - lg: 48px height
   * - sm: Compact 36px height
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Full width input
   */
  fullWidth?: boolean;

  /**
   * Icon to display at start of input
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to display at end of input
   */
  endIcon?: React.ReactNode;

  /**
   * Additional class names for the root element
   */
  className?: string;

  /**
   * Additional class names for the input element
   */
  inputClassName?: string;
}

/**
 * Input Component
 *
 * Laws:
 * - Height: sm=36, md=44, lg=48
 * - Radius: always 10px (radius-md)
 * - Border: 1px subtle (whisper, not loud)
 * - Focus: indigo border + 3px ring
 * - Forms must feel spacious: generous padding
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      optional = false,
      error,
      size = 'md',
      fullWidth = false,
      startIcon,
      endIcon,
      className,
      inputClassName,
      disabled,
      required,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const hasError = Boolean(error);

    // A controlled input must never receive `value={null}` (React warns and
    // treats it as uncontrolled). Coerce null to '' while leaving `undefined`
    // untouched so genuinely uncontrolled usage still works.
    const { value, ...inputProps } = props;
    const normalizedValue = value === null ? '' : value;

    return (
      <Field.Root
        className={clsx(styles.root, fullWidth && styles.fullWidth, className)}
        disabled={disabled}
        invalid={hasError}
      >
        {label && (
          <Field.Label htmlFor={inputId} className={styles.label}>
            {label}
            {required && <span className={styles.required}>*</span>}
            {optional && !required && <span className={styles.optional}>(Optional)</span>}
          </Field.Label>
        )}

        <div
          className={clsx(
            styles.inputWrapper,
            styles[size],
            hasError && styles.invalid,
            disabled && styles.disabled
          )}
        >
          {startIcon && <span className={styles.icon}>{startIcon}</span>}

          <BaseInput
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            className={clsx(styles.input, inputClassName)}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            value={normalizedValue}
            {...inputProps}
          />

          {endIcon && <span className={styles.icon}>{endIcon}</span>}
        </div>

        {error && (
          <div id={`${inputId}-error`} className={styles.error} role="alert">
            <CircleAlert size={14} className={styles.errorIcon} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {helperText && !error && (
          <Field.Description id={`${inputId}-helper`} className={styles.helper}>
            {helperText}
          </Field.Description>
        )}
      </Field.Root>
    );
  }
);

Input.displayName = 'Input';

export default Input;
